import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import {
  AppBar,
  Box,
  Button,
  Grid,
  Paper,
  Stack,
  Tab,
  Tabs,
  Toolbar,
  Typography,
} from '@mui/material';
import { Layout } from 'components';
import PopupEditACJobs from 'components/Popups/Jobs/PopupEditACJobs';
import { drawerWidth } from 'config';
import { convertDobToAge, hasPermission } from 'helpers';
import { useDateTime } from 'hooks';
import { POPUP_ACTION, TYPE_POPUP } from 'models';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { getJobsDetailAction } from 'store/reducers/jobs/actionTypes';
import AcJobTransactions from '../Transactions';
import { DetailItem } from './styles';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`nanny-detail-tabpanel-${index}`}
      aria-labelledby={`nanny-detail-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `nanny-detail-tab-${index}`,
    'aria-controls': `nanny-detail-tabpanel-${index}`,
  };
}

const tabs: { label: string; isDisable: boolean }[] = [
  {
    label: 'Transactions',
    isDisable: false,
  },
];

export const JobDetail = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id } = useParams();
  const { formatDateTime } = useDateTime();
  const [tabValue, setTabValue] = useState(0);
  const [popupAction, setPopupAction] = useState<POPUP_ACTION>();
  const { jobs_detail } = useAppSelector((state) => state.jobsReducer.jobs);

  const handleChangeTabValue = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const onShowPopupAction = (type: TYPE_POPUP) => {
    setPopupAction({ type: type });
  };

  const onClosePopupAction = () => {
    setPopupAction(undefined);
  };

  const onReloadDetail = () => {
    dispatch(getJobsDetailAction({ data: { id: id as any } }));
  };

  useEffect(() => {
    onReloadDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Layout>
      <AppBar
        position="fixed"
        sx={{
          width: `calc(100% - ${drawerWidth}px)`,
          ml: `${drawerWidth}px`,
          backgroundColor: 'white.light',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
        }}
        elevation={0}
      >
        <Toolbar
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Grid>
            <Typography
              sx={{
                fontSize: '20px',
                lineHeight: 1,
                color: 'grey1.light',
              }}
            >
              Jobs /{jobs_detail?.number}
            </Typography>
          </Grid>
        </Toolbar>
      </AppBar>
      <Paper elevation={0} sx={{ p: '16px' }}>
        <Grid container justifyContent="space-between" pb="16px">
          <Button
            startIcon={<ArrowBackIosNewIcon />}
            sx={{
              p: 0,
              color: 'black.light',
              ':hover': {
                backgroundColor: 'white.light',
              },
            }}
            variant="text"
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
        </Grid>
        <Stack spacing={2}>
          <Box
            sx={{
              backgroundColor: 'rgba(34, 111, 227, 0.04)',
              padding: '21px 31px',
            }}
          >
            <Stack direction="row" justifyContent="space-between">
              <Box>
                <Grid
                  container
                  rowSpacing={'8px'}
                  columnSpacing={'70px'}
                  mb={3}
                >
                  <DetailItem item>
                    <Typography
                      fontWeight={500}
                      lineHeight="14px"
                      fontSize={14}
                      letterSpacing="0.1px"
                      color="text.secondary"
                    >
                      Job ID
                    </Typography>
                    <Typography
                      fontWeight={400}
                      lineHeight="16px"
                      fontSize={14}
                      letterSpacing="0.25px"
                      color="black.light"
                    >
                      {jobs_detail?.number || 'No data to display yet'}
                    </Typography>
                  </DetailItem>
                  <DetailItem item>
                    <Typography
                      fontWeight={500}
                      lineHeight="14px"
                      fontSize={14}
                      letterSpacing="0.1px"
                      color="text.secondary"
                    >
                      Status
                    </Typography>
                    <Typography
                      fontWeight={400}
                      lineHeight="16px"
                      fontSize={14}
                      letterSpacing="0.25px"
                      color="black.light"
                      textTransform="capitalize"
                    >
                      {jobs_detail?.status || 'No data to display yet'}
                    </Typography>
                  </DetailItem>
                  <DetailItem item>
                    <Typography
                      fontWeight={500}
                      lineHeight="14px"
                      fontSize={14}
                      letterSpacing="0.1px"
                      color="text.secondary"
                    >
                      From
                    </Typography>
                    <Typography
                      fontWeight={400}
                      lineHeight="16px"
                      fontSize={14}
                      letterSpacing="0.25px"
                      color="black.light"
                    >
                      {formatDateTime(
                        jobs_detail?.start_datetime?.date,
                        'DD MMM YYYY'
                      )}
                      &nbsp;
                      {formatDateTime(
                        jobs_detail?.start_datetime?.time,
                        'HH:mm'
                      )}
                    </Typography>
                  </DetailItem>
                  <DetailItem item>
                    <Typography
                      fontWeight={500}
                      lineHeight="14px"
                      fontSize={14}
                      letterSpacing="0.1px"
                      color="text.secondary"
                    >
                      To
                    </Typography>
                    <Typography
                      fontWeight={400}
                      lineHeight="16px"
                      fontSize={14}
                      letterSpacing="0.25px"
                      color="black.light"
                    >
                      {formatDateTime(
                        jobs_detail?.end_datetime?.date,
                        'DD MMM YYYY'
                      )}
                      &nbsp;
                      {formatDateTime(jobs_detail?.end_datetime?.time, 'HH:mm')}
                    </Typography>
                  </DetailItem>
                </Grid>
                <Grid container rowSpacing={'8px'} columnSpacing={'70px'}>
                  <DetailItem item>
                    <Typography
                      fontWeight={500}
                      lineHeight="14px"
                      fontSize={14}
                      letterSpacing="0.1px"
                      color="text.secondary"
                    >
                      Address
                    </Typography>
                    <Typography
                      fontWeight={400}
                      lineHeight="16px"
                      fontSize={14}
                      letterSpacing="0.25px"
                      color="black.light"
                    >
                      {(jobs_detail?.address &&
                        `${jobs_detail?.address?.block_number} ${jobs_detail?.address.name} ${jobs_detail?.address.street} ${jobs_detail?.address?.unit_number}`) ||
                        'No data to display yet'}
                    </Typography>
                  </DetailItem>
                  <DetailItem item>
                    <Typography
                      fontWeight={500}
                      lineHeight="14px"
                      fontSize={14}
                      letterSpacing="0.1px"
                      color="text.secondary"
                    >
                      Children
                    </Typography>
                    <Typography
                      fontWeight={400}
                      lineHeight="16px"
                      fontSize={14}
                      letterSpacing="0.25px"
                      color="black.light"
                      textTransform="capitalize"
                    >
                      {jobs_detail?.children
                        .map(
                          (item) =>
                            `${item.full_name} (${convertDobToAge(
                              item.date_of_birth
                            )})`
                        )
                        .join(', ') || 'No data to display yet'}
                    </Typography>
                  </DetailItem>
                </Grid>
              </Box>
              {hasPermission(['superadmin', 'staff']) && (
                <Button
                  sx={{ height: '14px', fontSize: '14px' }}
                  variant="contained"
                  color="secondary"
                  onClick={() => onShowPopupAction(TYPE_POPUP.EDIT)}
                >
                  Edit Job
                </Button>
              )}
            </Stack>
          </Box>
        </Stack>
        <Grid
          container
          sx={{
            borderBottom: '1px solid  rgba(0, 0, 0, 0.08)',
            marginBottom: '16px',
          }}
        >
          <Tabs value={tabValue} onChange={handleChangeTabValue}>
            {tabs.map((item, index) => {
              return (
                <Tab
                  key={index}
                  label={item.label}
                  disabled={item.isDisable}
                  sx={{
                    fontSize: '16px',
                    fontWeight: 400,
                    lineHeight: '125%',
                    letterSpacing: '0.1px',
                    color: '#959595',
                    textTransform: 'capitalize',
                  }}
                  {...a11yProps(index)}
                />
              );
            })}
          </Tabs>
        </Grid>
        <TabPanel value={tabValue} index={0}>
          <AcJobTransactions jobId={jobs_detail?.id} />
        </TabPanel>
      </Paper>
      {popupAction?.type === TYPE_POPUP.EDIT && (
        <PopupEditACJobs
          open={popupAction?.type === TYPE_POPUP.EDIT}
          jobId={id}
          onSuccess={() => {
            onReloadDetail();
            onClosePopupAction();
          }}
          handleClosePopup={() => onClosePopupAction()}
        />
      )}
    </Layout>
  );
};

export default JobDetail;
